/**
 * financial-breakdown.service.ts - Rozklad finančního dopadu podle různých dimenzí
 * 
 * Poskytuje detailní analýzy nákladů podle:
 * - Typu závad (fault types)
 * - Směnových týmů (shift teams)
 * - Typů směn (shift types)
 * - Projektů
 * - Top cost drivers (nejdražší kombinace pracoviště + závada)
 */

import moment from 'moment';
import { PlanItemsRepository } from '@/libs/tpm-analytics/data/plan-items.repository';
import { ScrapRecordsRepository } from '@/libs/tpm-analytics/data/scrap-records.repository';
import { ShiftsRepository } from '@/libs/tpm-analytics/data/shifts.repository';
import {
    calcPlanItemDowntimeCostKc,
    calcPlanItemLostProfitKc,
    calcPlanItemMaintenanceSplitKc,
    calcScrapCostKc,
} from '@/libs/tpm-analytics/core/financial.calculator';

// ===== TypeScript interfaces =====

export interface FinancialImpactByFaultTypeItem {
    faultTypeId: string | null;
    faultTypeName: string;
    downtimeKc: number;
    maintenanceKc: number;
    scrapKc: number;
    lostProfitKc: number;
    totalKc: number;
    frequency: number; // počet výskytů
    avgCostPerOccurrence: number;
}

export interface FinancialImpactByShiftTeamItem {
    shiftTeamTypeId: string | null;
    shiftTeamTypeName: string;
    downtimeKc: number;
    maintenanceKc: number;
    scrapKc: number;
    lostProfitKc: number;
    totalKc: number;
}

export interface FinancialImpactByShiftTypeItem {
    shiftTypeId: string | null;
    shiftTypeName: string;
    downtimeKc: number;
    maintenanceKc: number;
    scrapKc: number;
    lostProfitKc: number;
    totalKc: number;
}

export interface FinancialImpactByProjectItem {
    projectId: string | null;
    projectName: string;
    downtimeKc: number;
    maintenanceKc: number;
    scrapKc: number;
    lostProfitKc: number;
    totalKc: number;
}

export interface TopCostDriverItem {
    workplaceId: string | null;
    workplaceName: string;
    faultTypeId: string | null;
    faultTypeName: string;
    frequency: number;
    downtimeKc: number;
    maintenanceKc: number;
    scrapKc: number;
    lostProfitKc: number;
    totalKc: number;
    avgCostPerOccurrence: number;
    trend: number | null; // % změna oproti předchozímu období
    items?: Array<{
        id: string;
        date: string;
        durationMinutes: number;
        costKc: number;
    }>;
}

export interface FinancialTimeOfDayHeatmapResult {
    grid: number[][]; // [7][24] - náklady podle dne v týdnu a hodiny
    totalCost: number;
    avgPerDay: number;
    criticalDayIndexes: number[]; // 0..6
    topHours: number[]; // top 3 hodiny
}

export interface CostPerOkPieceResult {
    totalCost: number;
    totalOkPieces: number;
    costPerPiece: number;
    costPerPieceBreakdown: {
        downtimePerPiece: number;
        maintenancePerPiece: number;
        scrapPerPiece: number;
        lostProfitPerPiece: number;
    };
}

// ===== Funkce =====

/**
 * Breakdown podle typu závad (audit fault type).
 * Vrací náklady agregované podle typu závady.
 */
export async function getFinancialImpactByFaultType(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
        faultTypeIds?: string[];
    }
): Promise<FinancialImpactByFaultTypeItem[]> {
    const [planItems, shiftScrapRecs] = await Promise.all([
        PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
            faultTypeIds: filters?.faultTypeIds,
        }),
        ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        }),
    ]);

    const map = new Map<string | null, FinancialImpactByFaultTypeItem>();

    // Pomocná funkce pro inicializaci záznamu
    const ensure = (faultId: string | null, faultName: string) => {
        if (!map.has(faultId)) {
            map.set(faultId, {
                faultTypeId: faultId,
                faultTypeName: faultName || '—',
                downtimeKc: 0,
                maintenanceKc: 0,
                scrapKc: 0,
                lostProfitKc: 0,
                totalKc: 0,
                frequency: 0,
                avgCostPerOccurrence: 0,
            });
        }
        return map.get(faultId)!;
    };

    // Prostoje, údržba a ušlý zisk z PlanItems
    for (const p of planItems as any[]) {
        const faultId: string | null = p?.auditFaultTypeId ?? p?.auditFaultType?.id ?? null;
        const faultName: string = p?.auditFaultType?.name ?? 'Neznámý typ závady';
        const e = ensure(faultId, faultName);
        
        e.downtimeKc += calcPlanItemDowntimeCostKc(p);
        e.lostProfitKc += calcPlanItemLostProfitKc(p);
        const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
        e.maintenanceKc += repairKc + maintenanceKc;
        e.frequency += 1;
    }

    // Zmetky z ShiftScrapRecords - nemají přímou vazbu na fault type
    // Agregujeme je pod speciální kategorii
    if (shiftScrapRecs.length > 0) {
        const scrapTotal = shiftScrapRecs.reduce((sum, r: any) => sum + calcScrapCostKc(r), 0);
        if (scrapTotal > 0) {
            const e = ensure('scrap', 'Zmetky (bez specifikace závady)');
            e.scrapKc += scrapTotal;
        }
    }

    // Vypočítáme celkové náklady a průměr
    for (const item of map.values()) {
        item.totalKc = item.downtimeKc + item.maintenanceKc + item.scrapKc + item.lostProfitKc;
        item.avgCostPerOccurrence = item.frequency > 0 ? item.totalKc / item.frequency : 0;
    }

    // Zaokrouhlíme a seřadíme podle celkových nákladů (sestupně)
    return Array.from(map.values())
        .map((item) => ({
            ...item,
            downtimeKc: Math.round(item.downtimeKc),
            maintenanceKc: Math.round(item.maintenanceKc),
            scrapKc: Math.round(item.scrapKc),
            lostProfitKc: Math.round(item.lostProfitKc),
            totalKc: Math.round(item.totalKc),
            avgCostPerOccurrence: Math.round(item.avgCostPerOccurrence),
        }))
        .sort((a, b) => b.totalKc - a.totalKc);
}

/**
 * Breakdown podle směnových týmů.
 */
export async function getFinancialImpactByShiftTeam(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
        faultTypeIds?: string[];
    }
): Promise<FinancialImpactByShiftTeamItem[]> {
    const [planItems, shiftScrapRecs] = await Promise.all([
        PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
            faultTypeIds: filters?.faultTypeIds,
        }),
        ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        }),
    ]);

    const map = new Map<string | null, FinancialImpactByShiftTeamItem>();

    const ensure = (teamId: string | null, teamName: string) => {
        if (!map.has(teamId)) {
            map.set(teamId, {
                shiftTeamTypeId: teamId,
                shiftTeamTypeName: teamName || '—',
                downtimeKc: 0,
                maintenanceKc: 0,
                scrapKc: 0,
                lostProfitKc: 0,
                totalKc: 0,
            });
        }
        return map.get(teamId)!;
    };

    // PlanItems - podle shift team type
    for (const p of planItems as any[]) {
        const teamId: string | null = p?.shiftTeamTypeId ?? null;
        const teamName: string = p?.shiftTeamType?.name ?? 'Bez týmu';
        const e = ensure(teamId, teamName);
        
        e.downtimeKc += calcPlanItemDowntimeCostKc(p);
        e.lostProfitKc += calcPlanItemLostProfitKc(p);
        const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
        e.maintenanceKc += repairKc + maintenanceKc;
    }

    // Zmetky - podle shift team type ze shift
    for (const r of shiftScrapRecs as any[]) {
        const shift = (r as any)?.shift;
        const teamId: string | null = shift?.shiftTeamTypeId ?? null;
        const teamName: string = shift?.shiftTeamType?.name ?? 'Bez týmu';
        const e = ensure(teamId, teamName);
        e.scrapKc += calcScrapCostKc(r);
    }

    // Celkové náklady
    for (const item of map.values()) {
        item.totalKc = item.downtimeKc + item.maintenanceKc + item.scrapKc + item.lostProfitKc;
    }

    return Array.from(map.values())
        .map((item) => ({
            ...item,
            downtimeKc: Math.round(item.downtimeKc),
            maintenanceKc: Math.round(item.maintenanceKc),
            scrapKc: Math.round(item.scrapKc),
            lostProfitKc: Math.round(item.lostProfitKc),
            totalKc: Math.round(item.totalKc),
        }))
        .sort((a, b) => b.totalKc - a.totalKc);
}

/**
 * Breakdown podle typů směn (ranní/odpolední/noční).
 */
export async function getFinancialImpactByShiftType(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
        faultTypeIds?: string[];
    }
): Promise<FinancialImpactByShiftTypeItem[]> {
    const [planItems, shiftScrapRecs] = await Promise.all([
        PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
            faultTypeIds: filters?.faultTypeIds,
        }),
        ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        }),
    ]);

    const map = new Map<string | null, FinancialImpactByShiftTypeItem>();

    const ensure = (typeId: string | null, typeName: string) => {
        if (!map.has(typeId)) {
            map.set(typeId, {
                shiftTypeId: typeId,
                shiftTypeName: typeName || '—',
                downtimeKc: 0,
                maintenanceKc: 0,
                scrapKc: 0,
                lostProfitKc: 0,
                totalKc: 0,
            });
        }
        return map.get(typeId)!;
    };

    // PlanItems - podle shift type
    for (const p of planItems as any[]) {
        const typeId: string | null = p?.shiftTypeId ?? null;
        const typeName: string = p?.shiftType?.name ?? 'Bez typu';
        const e = ensure(typeId, typeName);
        
        e.downtimeKc += calcPlanItemDowntimeCostKc(p);
        e.lostProfitKc += calcPlanItemLostProfitKc(p);
        const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
        e.maintenanceKc += repairKc + maintenanceKc;
    }

    // Zmetky - podle shift type ze shift
    for (const r of shiftScrapRecs as any[]) {
        const shift = (r as any)?.shift;
        const typeId: string | null = shift?.shiftTypeId ?? null;
        const typeName: string = shift?.shiftType?.name ?? 'Bez typu';
        const e = ensure(typeId, typeName);
        e.scrapKc += calcScrapCostKc(r);
    }

    // Celkové náklady
    for (const item of map.values()) {
        item.totalKc = item.downtimeKc + item.maintenanceKc + item.scrapKc + item.lostProfitKc;
    }

    return Array.from(map.values())
        .map((item) => ({
            ...item,
            downtimeKc: Math.round(item.downtimeKc),
            maintenanceKc: Math.round(item.maintenanceKc),
            scrapKc: Math.round(item.scrapKc),
            lostProfitKc: Math.round(item.lostProfitKc),
            totalKc: Math.round(item.totalKc),
        }))
        .sort((a, b) => b.totalKc - a.totalKc);
}

/**
 * Breakdown podle projektů.
 */
export async function getFinancialImpactByProject(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
        faultTypeIds?: string[];
    }
): Promise<FinancialImpactByProjectItem[]> {
    const [planItems, shiftScrapRecs] = await Promise.all([
        PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
            faultTypeIds: filters?.faultTypeIds,
        }),
        ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        }),
    ]);

    const map = new Map<string | null, FinancialImpactByProjectItem>();

    const ensure = (projectId: string | null, projectName: string) => {
        if (!map.has(projectId)) {
            map.set(projectId, {
                projectId: projectId,
                projectName: projectName || '—',
                downtimeKc: 0,
                maintenanceKc: 0,
                scrapKc: 0,
                lostProfitKc: 0,
                totalKc: 0,
            });
        }
        return map.get(projectId)!;
    };

    // PlanItems - podle projektu
    for (const p of planItems as any[]) {
        const projectId: string | null = p?.projectId ?? null;
        const projectName: string = p?.project?.name ?? 'Bez projektu';
        const e = ensure(projectId, projectName);
        
        e.downtimeKc += calcPlanItemDowntimeCostKc(p);
        e.lostProfitKc += calcPlanItemLostProfitKc(p);
        const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
        e.maintenanceKc += repairKc + maintenanceKc;
    }

    // Zmetky - podle produktu (z product.projectId)
    for (const r of shiftScrapRecs as any[]) {
        const productProjectId: string | null = (r as any)?.product?.projectId ?? null;
        const productProjectName: string = (r as any)?.product?.project?.name ?? 'Bez projektu';
        const e = ensure(productProjectId, productProjectName);
        e.scrapKc += calcScrapCostKc(r);
    }

    // Celkové náklady
    for (const item of map.values()) {
        item.totalKc = item.downtimeKc + item.maintenanceKc + item.scrapKc + item.lostProfitKc;
    }

    return Array.from(map.values())
        .map((item) => ({
            ...item,
            downtimeKc: Math.round(item.downtimeKc),
            maintenanceKc: Math.round(item.maintenanceKc),
            scrapKc: Math.round(item.scrapKc),
            lostProfitKc: Math.round(item.lostProfitKc),
            totalKc: Math.round(item.totalKc),
        }))
        .sort((a, b) => b.totalKc - a.totalKc);
}

/**
 * Top cost drivers - nejdražší kombinace pracoviště + typ závady.
 * Vrací top N záznamů s možností drill-down do jednotlivých výskytů.
 */
export async function getFinancialImpactTopDrivers(
    fromIso: string,
    toIso: string,
    topN = 20,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
        faultTypeIds?: string[];
    }
): Promise<TopCostDriverItem[]> {
    const planItems = await PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, {
        locationIds: filters?.locationIds,
        workplaceIds: filters?.workplaceIds,
        projectIds: filters?.projectIds,
        shiftTypeIds: filters?.shiftTypeIds,
        shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        faultTypeIds: filters?.faultTypeIds,
    });

    // Klíč: "workplaceId|faultTypeId"
    const map = new Map<string, TopCostDriverItem>();

    const getKey = (wpId: string | null, ftId: string | null) => `${wpId || 'null'}|${ftId || 'null'}`;

    const ensure = (wpId: string | null, wpName: string, ftId: string | null, ftName: string) => {
        const key = getKey(wpId, ftId);
        if (!map.has(key)) {
            map.set(key, {
                workplaceId: wpId,
                workplaceName: wpName || '—',
                faultTypeId: ftId,
                faultTypeName: ftName || '—',
                frequency: 0,
                downtimeKc: 0,
                maintenanceKc: 0,
                scrapKc: 0,
                lostProfitKc: 0,
                totalKc: 0,
                avgCostPerOccurrence: 0,
                trend: null,
                items: [],
            });
        }
        return map.get(key)!;
    };

    for (const p of planItems as any[]) {
        const wpId: string | null = p?.workplaceId ?? p?.workplace?.id ?? null;
        const wpName: string = p?.workplace?.name ?? '—';
        const ftId: string | null = p?.auditFaultTypeId ?? p?.auditFaultType?.id ?? null;
        const ftName: string = p?.auditFaultType?.name ?? '—';
        
        const e = ensure(wpId, wpName, ftId, ftName);
        
        const downKc = calcPlanItemDowntimeCostKc(p);
        const lostKc = calcPlanItemLostProfitKc(p);
        const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
        const totalCost = downKc + repairKc + maintenanceKc + lostKc;
        
        e.downtimeKc += downKc;
        e.maintenanceKc += repairKc + maintenanceKc;
        e.lostProfitKc += lostKc;
        e.frequency += 1;
        
        // Přidáme do drill-down items
        e.items!.push({
            id: p.id,
            date: p.shutdownFromDate || p.time || '',
            durationMinutes: Number(p.shutdownDuration ?? 0) || 0,
            costKc: Math.round(totalCost),
        });
    }

    // Vypočítáme celkové náklady a průměr
    for (const item of map.values()) {
        item.totalKc = item.downtimeKc + item.maintenanceKc + item.scrapKc + item.lostProfitKc;
        item.avgCostPerOccurrence = item.frequency > 0 ? item.totalKc / item.frequency : 0;
    }

    // Zaokrouhlíme, seřadíme a vrátíme top N
    return Array.from(map.values())
        .map((item) => ({
            ...item,
            downtimeKc: Math.round(item.downtimeKc),
            maintenanceKc: Math.round(item.maintenanceKc),
            scrapKc: Math.round(item.scrapKc),
            lostProfitKc: Math.round(item.lostProfitKc),
            totalKc: Math.round(item.totalKc),
            avgCostPerOccurrence: Math.round(item.avgCostPerOccurrence),
        }))
        .sort((a, b) => b.totalKc - a.totalKc)
        .slice(0, topN);
}

/**
 * Heatmapa nákladů podle času (den v týdnu × hodina).
 * Pomocná pro zobrazení, kdy vznikají nejvyšší náklady.
 */
export async function getFinancialTimeOfDayHeatmap(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
        faultTypeIds?: string[];
    }
): Promise<FinancialTimeOfDayHeatmapResult> {
    const planItems = await PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, {
        locationIds: filters?.locationIds,
        workplaceIds: filters?.workplaceIds,
        projectIds: filters?.projectIds,
        shiftTypeIds: filters?.shiftTypeIds,
        shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        faultTypeIds: filters?.faultTypeIds,
    });

    const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0)); // [7][24]
    let totalCost = 0;

    for (const p of planItems as any[]) {
        if (!p?.shutdownFromDate) continue;
        
        const downKc = calcPlanItemDowntimeCostKc(p);
        const lostKc = calcPlanItemLostProfitKc(p);
        const { repairKc, maintenanceKc } = calcPlanItemMaintenanceSplitKc(p);
        const cost = downKc + repairKc + maintenanceKc + lostKc;
        
        totalCost += cost;
        
        // Přiřadíme náklady ke dni a hodině začátku odstávky
        const from = moment(p.shutdownFromDate);
        const dayOfWeek = (from.isoWeekday() - 1 + 7) % 7; // 0=Po, 6=Ne
        const hour = from.hour();
        
        if (dayOfWeek >= 0 && dayOfWeek < 7 && hour >= 0 && hour < 24) {
            grid[dayOfWeek][hour] += cost;
        }
    }

    const from = moment(fromIso);
    const to = moment(toIso);
    const numDays = Math.max(1, to.diff(from, 'days'));
    const avgPerDay = totalCost / numDays;

    // Najdeme kritické dny (top 3 podle celkových nákladů)
    const daySums = grid.map((row) => row.reduce((sum, v) => sum + v, 0));
    const criticalDayIndexes = daySums
        .map((sum, idx) => ({ idx, sum }))
        .sort((a, b) => b.sum - a.sum)
        .slice(0, 3)
        .map((x) => x.idx);

    // Najdeme top 3 hodiny
    const hourSums = Array.from({ length: 24 }, (_, h) => grid.reduce((sum, row) => sum + row[h], 0));
    const topHours = hourSums
        .map((sum, idx) => ({ idx, sum }))
        .sort((a, b) => b.sum - a.sum)
        .slice(0, 3)
        .map((x) => x.idx);

    // Zaokrouhlíme hodnoty v gridu
    for (let d = 0; d < 7; d++) {
        for (let h = 0; h < 24; h++) {
            grid[d][h] = Math.round(grid[d][h]);
        }
    }

    return {
        grid,
        totalCost: Math.round(totalCost),
        avgPerDay: Math.round(avgPerDay),
        criticalDayIndexes,
        topHours,
    };
}

/**
 * Náklady na vyrobený kus (Cost per OK Piece).
 * Pomocná pro zobrazení, kolik stojí průměrná jednotka produkce.
 */
export async function getFinancialCostPerOkPiece(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
        faultTypeIds?: string[];
    }
): Promise<CostPerOkPieceResult> {
    const [planItems, shiftScrapRecs, shifts] = await Promise.all([
        PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
            faultTypeIds: filters?.faultTypeIds,
        }),
        ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        }),
        ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        }),
    ]);

    // Celkové náklady
    let downtimeKc = 0;
    let maintenanceKc = 0;
    let lostProfitKc = 0;
    
    for (const p of planItems as any[]) {
        downtimeKc += calcPlanItemDowntimeCostKc(p);
        lostProfitKc += calcPlanItemLostProfitKc(p);
        const { repairKc, maintenanceKc: mKc } = calcPlanItemMaintenanceSplitKc(p);
        maintenanceKc += repairKc + mKc;
    }

    const scrapKc = shiftScrapRecs.reduce((sum, r: any) => sum + calcScrapCostKc(r), 0);

    const totalCost = downtimeKc + maintenanceKc + scrapKc + lostProfitKc;

    // Celkový počet OK kusů
    const totalOkPieces = shifts.reduce((sum, s: any) => sum + (Number(s.okCount ?? 0) || 0), 0);

    const costPerPiece = totalOkPieces > 0 ? totalCost / totalOkPieces : 0;

    return {
        totalCost: Math.round(totalCost),
        totalOkPieces,
        costPerPiece: Math.round(costPerPiece * 100) / 100, // 2 desetinná místa
        costPerPieceBreakdown: {
            downtimePerPiece: totalOkPieces > 0 ? Math.round((downtimeKc / totalOkPieces) * 100) / 100 : 0,
            maintenancePerPiece: totalOkPieces > 0 ? Math.round((maintenanceKc / totalOkPieces) * 100) / 100 : 0,
            scrapPerPiece: totalOkPieces > 0 ? Math.round((scrapKc / totalOkPieces) * 100) / 100 : 0,
            lostProfitPerPiece: totalOkPieces > 0 ? Math.round((lostProfitKc / totalOkPieces) * 100) / 100 : 0,
        },
    };
}

